/* NP_MOTION (first version, restored Nov 3, 2026): staggered scroll reveal + view-transition housekeeping.
   - Only elements that start below the fold are hidden, so nothing visible ever flashes.
   - Only leaf elements are moved; layout-measuring sections get opacity-only (npm-fade).
   - Classes are removed after the reveal, so no transform is left behind. */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Skip the cross-fade on back/forward: phones already animate their own swipe-back
     and the page should come back instantly. */
  window.addEventListener('pagereveal', function (e) {
    if (!e.viewTransition) return;
    var type = '';
    try {
      if (window.navigation && navigation.activation) type = navigation.activation.navigationType;
      if (!type) {
        var nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type === 'back_forward') type = 'traverse';
      }
    } catch (err) {}
    if (type === 'traverse' || type === 'reload') e.viewTransition.skipTransition();
  });

  if (!('IntersectionObserver' in window)) return;
  if (reduce && reduce.matches) return;
  if (window.Shopify && Shopify.designMode) return;

  /* [selector, group (stagger together), opacity-only] */
  var TARGETS = [
    // Section headings
    ['.np-bestsellers__header', 'head', false],
    ['.np-why__header', 'head', false],
    ['.np-ugc__heading, .np-ugc__subheading', 'head', false],
    ['.np-customer-world__eyebrow, .np-customer-world__heading', 'head', false],
    ['.np-rec-section__heading, .np-rec-section__subtitle', 'head', false],
    // Cards and tiles
    ['.np-bestseller-card', 'grid', false],
    ['.np-showcase-card', 'grid', false],        // inner card; .np-showcase-item stays put for row equalizing
    ['.np-rec-card', 'grid', false],
    ['.np-ugc-slide', 'grid', false],
    ['.np-why-item', 'grid', false],
    ['.np-cat-card', 'grid', true],              // arrows are positioned from these, so fade only
    ['.np-why__photos', 'media', true]
  ];

  var vh = 0;
  var mobile = window.matchMedia('(max-width: 749px)').matches;
  var maxStagger = mobile ? 4 : 6;
  var step = mobile ? 40 : 55;

  var observer = new IntersectionObserver(onEnter, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

  function prepare() {
    vh = window.innerHeight || document.documentElement.clientHeight;
    // Background tab, prerender or zero-size viewport: we can't tell what's above
    // the fold, so wait until the page is actually shown (or skip reveals entirely).
    if (document.visibilityState !== 'visible' || vh < 100) {
      if (!prepare.waiting) {
        prepare.waiting = true;
        document.addEventListener('visibilitychange', function onVis() {
          if (document.visibilityState !== 'visible') return;
          document.removeEventListener('visibilitychange', onVis);
          if (window.scrollY < 50 && (window.innerHeight || 0) >= 100) prepare();
        });
      }
      return;
    }
    TARGETS.forEach(function (t) {
      var list = document.querySelectorAll(t[0]);
      for (var i = 0; i < list.length; i++) {
        var el = list[i];
        if (el.classList.contains('npm-hide') || el.closest('.npm-hide')) continue;
        if (el.closest('[data-np-review-globe], .nprg-globe-section, [data-np-globe-stage], dialog, [role="dialog"]')) continue;
        var r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue;     // hidden tab panel or collapsed
        if (r.top < vh) continue;                           // already on screen: never hide
        el.classList.add('npm-hide');
        if (t[2]) el.classList.add('npm-fade');
        el.setAttribute('data-npm-group', t[1]);
        observer.observe(el);
      }
    });
  }

  var batch = [];
  var scheduled = false;

  function onEnter(entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      if (batch.indexOf(entry.target) === -1) batch.push(entry.target);
      // Horizontal sliders: cards clipped to the side never "intersect",
      // so bring in the hidden siblings that sit on the same row.
      var parent = entry.target.parentElement;
      if (!parent) return;
      var top = entry.rootBounds ? entry.rootBounds.bottom : window.innerHeight;
      for (var i = 0; i < parent.children.length; i++) {
        var sib = parent.children[i];
        if (sib === entry.target || !sib.classList.contains('npm-hide') || batch.indexOf(sib) !== -1) continue;
        if (sib.getBoundingClientRect().top < top) {
          observer.unobserve(sib);
          batch.push(sib);
        }
      }
    });
    if (batch.length && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  }

  function flush() {
    scheduled = false;
    var counters = {};
    // Reveal in visual order (top, then left) so the stagger reads naturally
    batch.sort(function (a, b) {
      var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
      return (ra.top - rb.top) || (ra.left - rb.left);
    });
    batch.forEach(function (el) {
      var parent = el.parentElement;
      var key = (el.getAttribute('data-npm-group') || '') + ':' + (parent && parent.parentElement ? indexOf(parent.parentElement) : 0);
      var n = counters[key] = (counters[key] || 0) + 1;
      var delay = Math.min(n - 1, maxStagger) * step;
      el.style.setProperty('--npm-delay', delay + 'ms');
      el.classList.add('npm-in');
      cleanupLater(el, delay);
    });
    batch = [];
  }

  var ids = new WeakMap(), nextId = 1;
  function indexOf(node) {
    if (!ids.has(node)) ids.set(node, nextId++);
    return ids.get(node);
  }

  function cleanupLater(el, delay) {
    var done = false;
    function clear() {
      if (done) return;
      done = true;
      el.classList.remove('npm-hide', 'npm-fade', 'npm-in');
      el.style.removeProperty('--npm-delay');
      el.removeAttribute('data-npm-group');
    }
    el.addEventListener('transitionend', function (e) {
      if (e.target === el && e.propertyName === 'opacity') clear();
    });
    setTimeout(clear, delay + 700); // fallback
  }

  /* If reduced motion is switched on mid-visit, reveal everything at once */
  if (reduce && reduce.addEventListener) {
    reduce.addEventListener('change', function () {
      if (!reduce.matches) return;
      observer.disconnect();
      document.querySelectorAll('.npm-hide').forEach(function (el) {
        el.classList.remove('npm-hide', 'npm-fade', 'npm-in');
      });
    });
  }

  /* Anything printed or opened with find-in-page still needs to be readable */
  window.addEventListener('beforeprint', function () {
    document.querySelectorAll('.npm-hide').forEach(function (el) { el.classList.remove('npm-hide', 'npm-fade'); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prepare, { once: true });
  } else {
    prepare();
  }
})();
